// Hook to fetch the /tenantApp

/*  Shared  */
import { Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import i18n from 'i18n';

/*  React  */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const { t } = i18n;

const defaultObject = {};
// Always define the params object outside the component using it, otherwise
// you will keep rendering and re-rendering the component.
/**
 * Hook to fetch tenant application links
 * @param {Object} options - The options object
 * @param {string|null|undefined} options.clientId - Optional client ID to filter results
 * @param {Object} options.params - Optional parameters object for the API request
 * @returns {{
 *   loading: boolean,
 *   refetchTenantApps: () => void,
 *   singleTenantApp: import('application/Shared/types/tenantApp').TenantApp,
 *   tenantAppCount: number,
 *   tenantApps: Array
 * }} Object containing:
 *   - loading {boolean} - Loading state
 *   - refetchTenantApps {function} - Function to trigger a refetch
 *   - singleTenantApp {Object} - Single tenant app data if found
 *   - tenantAppCount {number} - Total count of tenant apps
 *   - tenantApps {Array} - Array of tenant apps
 */
export const useFetchTenantApplicationLink = ({
    clientId = null,
    params = defaultObject
}) => {
    const [tenantApps, setTenantApp] = useState([]);
    const [singleTenantApp, setSingleTenantApp] = useState(false);
    const [tenantAppCount, setTenantAppCount] = useState(0);
    const [loading, setLoading] = useState(true);
    // calling refetchTenantApps will force a rerender on the hook
    const [shouldRefetchTenantApps, setShouldRefetchTenantApps] = useState({});

    const refetchTenantApps = () => setShouldRefetchTenantApps({});

    const parameters = useMemo(
        () =>
            clientId
                ? {
                      ...params,
                      includeClients: true,
                      platformTenantId: clientId
                  }
                : params,
        [clientId, params]
    );

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get('/tenantApp', {
                    cancelToken: source.token,
                    params: parameters
                });
                setTenantAppCount(
                    response.data?.data?.count
                        ? parseInt(response.data.data.count, 10)
                        : 0
                );
                if (!parameters.count) {
                    if (!Response.hasData(response)) {
                        debug.e('No tenant app link found.');
                    } else {
                        setSingleTenantApp(Response.getOne(response));
                        const appsResponse = Response.getData(response);
                        setTenantApp(appsResponse);
                    }
                }
            } catch (error) {
                debug.e('Error Getting Tenant Application Link.', error);
            }
            setLoading(false);
        };

        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [parameters, params.count, shouldRefetchTenantApps]);

    return {
        loading,
        // returning the refetchTenantApps function so we can call it as needed.
        refetchTenantApps,
        singleTenantApp,
        tenantAppCount,
        tenantApps
    };
};

// Hook to fetch the getOne /tenantApp data async
export const useFetchSingleTenantAppAsync = () => {
    const error = useRef(null);
    const tenantData = useRef({});

    const getTenantApp = useCallback(async (params) => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        try {
            const response = await axios.get('/tenantApp', {
                cancelToken: source.token,
                params
            });
            const data = Response.getOne(response);
            if (data) {
                tenantData.current = data;
            } else {
                tenantData.current = {};
            }
        } catch (err) {
            error.current = err;
            debug.e('Error Getting Single Tenant App Information.', err);
        }
        return {
            error: error.current,
            tenantData: tenantData.current
        };
    }, []);
    return { getTenantApp };
};

export const usePutTenantApplicationLink = () => {
    const [saving, setSaving] = useState(false);

    const putTenantApplicationLink = useCallback(
        async ({ id, params = {} }) => {
            setSaving(true);

            let response;
            try {
                response = await axios.put(`/tenantApp/${id}`, params);
            } catch (err) {
                setSaving(false);
                const errMsg = err?.data?.message || t('Common.error');
                throw new Error(errMsg);
            }

            setSaving(false);

            return response;
        },
        []
    );

    return { putTenantApplicationLink, saving };
};
